import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import Button from "@/components/ui/Button";
import { ButtonStyleType, UIComponentSize } from "@/types/shared/uiComponents";
import type { EndorsementType } from "@/types";
import { useEndorsements } from "../hooks/useEndorsements";
import { EndorsementTypePicker } from "./EndorsementTypePicker";
import { EndorsementConfigForm } from "./EndorsementConfigForm";
import { getDefaultConfig } from "./endorsementFieldSchemas";

type CreateStep = "pick-type" | "configure";

interface EndorsementCreatePanelProps {
  policyId: string;
  /** When true, endorsements follow the policy status — skip date-based active check */
  policyIsActive?: boolean;
  /** Policy effective start date (YYYY-MM-DD) — used to default valid period */
  policyStartDate?: string | null;
  /** Policy effective end date (YYYY-MM-DD) — used to default valid period */
  policyEndDate?: string | null;
  /** Called after successful save — parent closes the drawer */
  onSave: () => void;
}

/**
 * Two-step create flow for adding a new endorsement:
 * 1. Pick endorsement type from categorized list
 * 2. Fill in type-specific config form
 *
 * Rendered inside the SideDrawer when DrawerContent is { type: "endorsement-create" }.
 */
export const EndorsementCreatePanel = ({
  policyId,
  policyIsActive = false,
  policyStartDate,
  policyEndDate,
  onSave,
}: EndorsementCreatePanelProps) => {
  const { endorsements, createEndorsement } = useEndorsements(policyId, policyIsActive);
  const [step, setStep] = useState<CreateStep>("pick-type");
  const [selectedType, setSelectedType] = useState<EndorsementType | null>(null);

  const handleTypeSelected = (type: EndorsementType) => {
    setSelectedType(type);
    setStep("configure");
  };

  const handleBack = () => {
    setStep("pick-type");
    setSelectedType(null);
  };

  const handleSubmit = (data: Record<string, unknown>) => {
    if (!selectedType) return;
    createEndorsement(selectedType, data, onSave);
  };

  // Step 1: Type picker
  if (step === "pick-type") {
    return (
      <Box sx={{ px: 0.5 }}>
        <EndorsementTypePicker onTypeSelected={handleTypeSelected} endorsements={endorsements} />
      </Box>
    );
  }

  // Step 2: Config form
  if (!selectedType) return null;

  // Merge type-specific defaults with policy dates for the valid period
  const defaults = {
    ...getDefaultConfig(selectedType),
    ...(policyStartDate ? { valid_period_start: policyStartDate } : {}),
    ...(policyEndDate ? { valid_period_end: policyEndDate } : {}),
  };

  return (
    <Box>
      {/* Back navigation */}
      <Button
        variant={ButtonStyleType.Text}
        size={UIComponentSize.Small}
        startIcon={<ArrowBack sx={{ fontSize: 14 }} />}
        onClick={handleBack}
        sx={{ mb: 1.5, textTransform: "none", px: 0.5 }}
      >
        Back to type selection
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, px: 0.5 }}>
        Creating new endorsement
      </Typography>
      <Typography variant="body1" fontWeight={600} sx={{ mb: 2, px: 0.5 }}>
        {formatToTitleCase(selectedType)}
      </Typography>

      <EndorsementConfigForm
        endorsementType={selectedType}
        defaultValues={defaults}
        onSubmit={handleSubmit}
        onCancel={handleBack}
        submitLabel="Create Endorsement"
        policyId={policyId}
        showJunctionFields
      />
    </Box>
  );
};
